import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import * as api from '../../utils/api';
import { setClient } from '../../actions/client';
import Container from '../../components/shared/atoms/Container';
import Box from '../../components/shared/atoms/Box';
import PriceCard from '../../components/shared/PriceCard';
import Row from '../../components/shared/atoms/Row';
import Text from '../../components/shared/atoms/Text';
import Heading from '../../components/shared/atoms/Heading';
import { Content } from '../../components/shared/organisms/HowItWorks';
import Separator from '../../components/Separator';
import WeTellYouEverything from '../../components/shared/molecules/WeTellYouEverything';
import MaintainPleasure from '../../components/shared/organisms/MaintainPleasure';
import AddGiftCardHeroBox from '../../components/giftcard/addGiftcard/AddGiftCardHeroBox';
import LoginModal from '../../components/giftcard/LoginModal';

const AddGiftCard = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const client = useSelector((s) => s.client);
  const [giftcard, setGiftcard] = useState('');
  const [errorAdd, setErrorAdd] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const addCard = async () => {
    setLoading(true);
    const rep = await api.post('/giftcards/use', {
      code: giftcard,
    });
    if (rep.error_message) {
      setErrorAdd(rep.error_message);
    } else {
      const fetchClient = await api.get(`/clients/${client.id}`);
      setSuccess(true);
      dispatch(setClient(fetchClient));
    }
    setLoading(false);
  };

  return (
    <Container fluid>
      <Helmet
        title="Activer votre carte cadeau | Roulez Jeunesse"
        meta={[
          {
            name: 'description',
            content:
              'Activez votre carte cadeau Roulez Jeunesse pour profiter de réparations vélo à domicile.',
          },
          { name: 'twitter:card', content: 'summary_large_image' },
          {
            name: 'twitter:title',
            content: 'Activer votre carte cadeau | Roulez Jeunesse',
          },
          {
            name: 'twitter:description',
            content:
              'Activez votre carte cadeau Roulez Jeunesse pour profiter de réparations vélo à domicile.',
          },
          {
            name: 'twitter:image',
            content:
              'https://uploads-ssl.webflow.com/664e20aae5145eb7a61de885/666854745cce5ad6c682189f_Re%CC%81parer%20son%20ve%CC%81lo%20devient%20un%20jeu%20d%E2%80%99enfant.png',
          },
        ]}
      />
      <LoginModal
        showModal={showModal}
        setShowModal={setShowModal}
        addCard={addCard}
        titleSignup={t('giftcardadd.signup.title')}
        ctaLabelSignup={t('giftcardadd.signup.cta')}
        titleSignin={t('giftcardadd.login.title')}
        ctaLabelSignin={t('giftcardadd.login.cta')}
      />
      <Box zIndex={0} display={{ xs: 'none', md: 'block' }}>
        <img
          src="/static/img/photos/giftcard/buy_gift_card_banner.jpg"
          alt="Activer sa carte cadeau réparation vélo Roulez Jeunesse"
          style={{
            width: '100%',
            maxHeight: 380,
            objectFit: 'cover',
            borderRadius: 24,
          }}
        />
      </Box>
      <Heading level={1} textAlign="center" mt={7}>
        {t('addgiftcard.heading')}
      </Heading>
      <Container style={{ zIndex: 1, position: 'relative' }}>
        <AddGiftCardHeroBox
          giftcard={giftcard}
          setGiftcard={setGiftcard}
          client={client}
          errorAdd={errorAdd}
          setErrorAdd={setErrorAdd}
          success={success}
          setShowModal={setShowModal}
          loading={loading}
          addCard={addCard}
        />
        <Content title={t('howitworks')} mt={10} t={t} />
        <Separator axe="horizontal" my={9} />
        <Heading level={3} textAlign="center">
          {t('giftcardadd.titles.1')}
        </Heading>
        <Text textAlign="center" mb={5}>
          {t('giftcardadd.subtitles.1')}
        </Text>
        <Row md>
          <PriceCard
            boxShadow="md"
            title={t('giftcardadd.titles.2')}
            image="/static/img/photos/pneu-creve-velo.png"
            text={t('giftcardadd.texts.2')}
            link="/reparation"
            price="25"
            bg="white"
          />
          <PriceCard
            boxShadow="md"
            title={t('giftcardadd.titles.3')}
            image="/static/img/photos/frein-use-velo.png"
            text={t('giftcardadd.texts.3')}
            link="/reparation"
            price="27"
            bg="white"
          />
          <PriceCard
            boxShadow="md"
            title={t('giftcardadd.titles.4')}
            image="/static/img/photos/giftcard/image-autumn.jpg"
            text={t('giftcardadd.texts.4')}
            link="/reparation"
            price="50"
            textPrice={false}
            bg="white"
          />
        </Row>
        <Separator axe="horizontal" my={9} />
        <WeTellYouEverything
          giftcard
          values={[
            {
              title: t('giftcard.questions.1'),
              text: <span>{t('giftcard.answers.1')}</span>,
            },
            {
              title: t('giftcard.questions.2'),
              text: t('giftcard.answers.2'),
            },
            {
              title: t('giftcard.questions.3'),
              text: t('giftcard.answers.3'),
            },
            {
              title: <span>{t('giftcard.questions.4')}</span>,
              text: t('giftcard.answers.4'),
            },
            {
              title: <span>{t('giftcard.questions.5')}</span>,
              text: t('giftcard.answers.5'),
            },
            {
              title: <span>{t('giftcard.questions.6')}</span>,
              text: t('giftcard.answers.6'),
            },
          ]}
        />
      </Container>
    </Container>
  );
};

export default AddGiftCard;
